// lib/list/listGenerationTelemetry.ts
import type { GenerationTask, KernelState, ProjectRuntimeState } from '#types/types';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';
import { listGenerationRecoveryStrategy } from './listGenerationRepair';

const EMPTY_TASKS: GenerationTask[] = [];

export function isListTaskRecovering(task: Pick<GenerationTask, 'error' | 'failureCode' | 'failureDetails' | 'listAttempt' | 'recoveryPending'>) {
  return Boolean(task.error && !task.recoveryPending && listGenerationRecoveryStrategy(task)
    && task.listAttempt?.active && task.listAttempt.number <= task.listAttempt.limit);
}

export function selectChatProjectGenerationTasks(state: Pick<KernelState, 'activeChatSessionId' | 'inventory' | 'entityKernelState'>) {
  const projectId = selectActiveSessionProjectIdFromInventoryState(state);
  const runtime = projectId ? state.entityKernelState[`Project:${projectId}`]?.state?.runtime as ProjectRuntimeState | undefined : undefined;
  return runtime?.generationTasks ?? EMPTY_TASKS;
}

export function listLiveGenerationTasks(tasks: GenerationTask[], projectId: string | null, sessionId: string | null) {
  return tasks.filter(task => task.projectId === projectId && (!task.chatSessionId || task.chatSessionId === sessionId)
    && !task.layer && !task.isOutcomeReported && (!task.error || task.recoveryPending || isListTaskRecovering(task)));
}

export function listGenerationStatus(task: Pick<GenerationTask, 'lastStatusConfirmedAt' | 'startedAt' | 'loopStepLabel' | 'recoveryPending' | 'listAttempt' | 'error' | 'failureCode' | 'failureDetails'>, now: number) {
  // Before the server's first confirmation, a job that just started is generating, not in doubt.
  const checkedAt = task.lastStatusConfirmedAt ?? task.listAttempt?.startedAt ?? task.startedAt;
  const age = typeof checkedAt === 'number' && Number.isFinite(checkedAt) && checkedAt > 0
    ? Math.max(0, Math.floor((now - checkedAt) / 1000)) : null;
  const checking = !isListTaskRecovering(task) && (age === null || age >= 30 || Boolean(task.recoveryPending));
  return { phase: ['Troubleshooting…', 'Waiting for the model…'].includes(task.loopStepLabel ?? '') && isListTaskRecovering(task) ? task.loopStepLabel! : checking ? 'Checking generation…' : 'Generating…' };
}
